import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Footer from "../../../components/landing/Footer";
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";

const RefundPolicy: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const policies = [
    {
      title: "Long-Term Stays (Monthly)",
      items: [
        "Cancellation 30+ days before check-in: Full refund of advance payment.",
        "Cancellation 15–29 days before check-in: 50% refund of advance payment.",
        "Cancellation less than 15 days before check-in: No refund.",
        "After check-in: No refund for the current month's rent.",
      ],
    },
    {
      title: "Short-Term Stays (Nightly/Weekly)",
      items: [
        "Cancellation 7+ days before check-in: Full refund.",
        "Cancellation 3–6 days before check-in: 50% refund.",
        "Cancellation less than 3 days before check-in: No refund.",
        "Early check-out: No refund for unused nights.",
      ],
    },
    {
      title: "Host Cancellations",
      items: [
        "If a host cancels a confirmed booking, guests receive a 100% full refund.",
        "Refund is processed within 3–5 business days.",
        "Social Bunkr will assist in finding alternative accommodation.",
      ],
    },
    {
      title: "Refund Processing",
      items: [
        "Refunds are credited to the original payment method used.",
        "Processing time: 5–7 business days after approval.",
        "UPI refunds typically arrive within 1–3 business days.",
        "Credit/Debit card refunds may take 5–10 business days depending on your bank.",
      ],
    },
    {
      title: "Non-Refundable Situations",
      items: [
        "GST charged on bookings is non-refundable.",
        "Platform convenience fees (if any) are non-refundable.",
        "Bookings marked as 'Non-Refundable' at the time of booking.",
        "Damages or violations of house rules that result in early termination.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.02 }}>
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="text-foreground/80 hover:text-primary font-medium transition-colors">
                  {item}
                </Link>
              ))}
            </nav>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" className="text-foreground hover:text-primary">Login</Button></Link>
              <Link to="/signup"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Sign Up</Button></Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden py-4 space-y-3">
              {["Home", "About", "Contact"].map((item) => (
                <Link key={item} to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                  className="block text-foreground/80 hover:text-primary font-medium transition-colors py-2">
                  {item}
                </Link>
              ))}
            </motion.div>
          )}
        </div>
      </motion.header>

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl font-bold text-foreground mb-2">Refund Policy</h1>
            <p className="text-muted-foreground mb-8">Last updated: January 1, 2025</p>

            <div className="bg-background rounded-2xl shadow-sm p-8">
              <p className="text-foreground/80 leading-relaxed mb-8">
                At Social Bunkr, we strive to make cancellations and refunds as fair and transparent as possible.
                Please review the policy applicable to your booking type below.
              </p>

              <div className="space-y-8">
                {policies.map((policy) => (
                  <div key={policy.title}>
                    <h2 className="text-lg font-semibold text-foreground mb-3 text-primary">{policy.title}</h2>
                    <ul className="space-y-2">
                      {policy.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-foreground/70">
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Note:</strong> Cancellation policies may vary by property.
                  Always check the specific policy on the property listing before booking. In case of disputes,
                  Social Bunkr's decision shall be final.
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground text-sm mb-4">
                Need to request a refund or have a question about your booking?
              </p>
              <Link to="/contact">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                  Contact Support
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RefundPolicy;
